import { AbTestStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import DuplicateIcon from "@redotech/redo-icons/copy-01.svg";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { DATE_FORMAT } from "@redotech/redo-web/time";
import { Handle, Position } from "@xyflow/react";
import { PartialWithRequiredType } from "../steps";
import { getStepCreatedAt } from "../utils/get-step-created-at";
import { BaseNode } from "./base-node";
import * as NodeCSS from "./node.module.css";

export interface AbTestNodeData {
  step: PartialWithRequiredType<AbTestStep>;
  deleteElements: (id: string) => void;
}

export function AbTestNode(data: {
  id: string;
  data: AbTestNodeData;
  selected?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const { step, deleteElements } = data.data;
  const createdAt = getStepCreatedAt(step.id);

  return (
    <BaseNode
      icon={<RedoFeaturedIcon color="warning" Icon={DuplicateIcon} size="xs" />}
      onDelete={() => deleteElements(data.id)}
      selected={data.selected}
      step={step}
      title={step.name || t("Weighted branch")}
    >
      <Handle position={Position.Top} type="target" />
      <Flex
        className={NodeCSS.fullWidth}
        dir="column"
        gap="md"
        justify="flex-start"
      >
        <Text fontSize="xxs">{t("Variations")}</Text>
        {step.variants && step.variants.length > 0 ? (
          <Text className={NodeCSS.valueText}>{step.variants.length}</Text>
        ) : (
          <Text className={NodeCSS.placeholderText}>
            {t("Needs configuration")}
          </Text>
        )}
        {createdAt && (
          <Text className={NodeCSS.placeholderText} fontSize="xxs">
            {t("Created {{date}}", { date: DATE_FORMAT.format(createdAt) })}
          </Text>
        )}
      </Flex>
      {/* Create a source handle for each variant */}
      {step.variants && step.variants.length > 0 ? (
        step.variants.map((variant, index) => (
          <Handle
            id={variant.id}
            key={variant.id}
            position={Position.Bottom}
            type="source"
          />
        ))
      ) : (
        <Handle position={Position.Bottom} type="source" />
      )}
    </BaseNode>
  );
}
